import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";

import { Layout } from "@/components/Layout";
import { buildSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CMS_PASSWORD, CMS_STORAGE_KEY } from "@/lib/game-cms";
import { GamesAdmin } from "@/components/admin/GamesAdmin";
import { NewsletterAdmin } from "@/components/admin/NewsletterAdmin";

type AdminTab = "games" | "newsletter";

export const Route = createFileRoute("/admin/")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>): { tab: AdminTab } => ({
    tab: s['tab'] === "newsletter" ? "newsletter" : "games",
  }),
  head: () =>
    buildSeo({
      title: "Admin CMS",
      description: "Internal content management dashboard.",
      path: "/admin",
      noindex: true,
    }),
  component: AdminRoute,
});

function AdminRoute() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");

  useEffect(() => {
    setUnlocked(sessionStorage.getItem(CMS_STORAGE_KEY) === "1");
  }, []);

  if (!unlocked) {
    return (
      <Layout>
        <div className="container flex items-center justify-center py-32">
          <form
            className="w-full max-w-sm space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (password === CMS_PASSWORD) {
                sessionStorage.setItem(CMS_STORAGE_KEY, "1");
                setUnlocked(true);
              } else {
                toast.error("Incorrect password");
              }
            }}
          >
            <div className="flex items-center justify-center gap-2 text-xl font-semibold">
              <Lock className="h-5 w-5" />
              Admin CMS
            </div>
            <Input
              type="password"
              placeholder="Admin password"
              aria-label="Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
            />
            <Button type="submit" className="w-full" disabled={!password}>
              Unlock
            </Button>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container space-y-6 py-8">
        <h1 className="text-2xl font-bold">Admin CMS</h1>
        <Tabs
          value={tab}
          onValueChange={(v) =>
            navigate({ to: "/admin", search: { tab: v as AdminTab }, replace: true })
          }
        >
          <TabsList>
            <TabsTrigger value="games">Games &amp; tools</TabsTrigger>
            <TabsTrigger value="newsletter">Newsletter &amp; site</TabsTrigger>
          </TabsList>
          <TabsContent value="games" className="mt-6">
            <GamesAdmin />
          </TabsContent>
          <TabsContent value="newsletter" className="mt-6">
            <NewsletterAdmin />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
